import { useEffect, useState } from "react";
import { toast } from "sonner";

import { adminEndpoints, absoluteUrl, extractErrorAr } from "@/lib/adminApi";
import Icon from "@/components/Icon";
import ImageField from "@/components/admin/ImageField";

/**
 * Generic CRUD page for a resource.
 *
 * Props:
 * - title, resource ('products'|'categories'|'services'|'projects'|'testimonials')
 * - emptyItem: object with default values for a new row
 * - columns: [{key,label,render?(row)}] shown in the table
 * - fields: array of field descriptors describing the modal form:
 *     {name, label, type: 'text'|'textarea'|'number'|'select'|'checkbox'|'image'|'list'|'kv-list',
 *      options?, help?, min?, max?, itemLabel? (kv-list), rows?}
 * - transformOut?: (form) => payload sent to server (for computed slug etc.)
 * - loadExtra?: fn returning promise to load extra data (categories for products)
 * - testIdPrefix
 */
export default function ResourceAdmin({
  title,
  resource,
  emptyItem,
  columns,
  fields,
  transformOut = (x) => x,
  loadExtra = null,
  testIdPrefix = "admin-resource",
  toolbar = null,
  filterRow = null,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extra, setExtra] = useState({});
  const [editing, setEditing] = useState(null); // { mode:'create'|'edit', item }
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const crud = adminEndpoints.crud(resource);

  const visibleItems = filterRow ? items.filter(filterRow) : items;

  const load = async () => {
    setLoading(true);
    try {
      const list = await crud.list();
      setItems(list);
      if (loadExtra) setExtra(await loadExtra());
    } catch (err) {
      toast.error(extractErrorAr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  const openCreate = () => setEditing({ mode: "create", item: { ...emptyItem } });
  const openEdit = (item) => setEditing({ mode: "edit", item: { ...emptyItem, ...item } });
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = transformOut(editing.item);
      if (editing.mode === "create") {
        await crud.create(payload);
        toast.success("تم الإنشاء بنجاح.");
      } else {
        await crud.update(editing.item.id, payload);
        toast.success("تم الحفظ.");
      }
      close();
      await load();
    } catch (err) {
      toast.error(extractErrorAr(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await crud.remove(confirmDel.id);
      toast.success("تم الحذف.");
      setConfirmDel(null);
      await load();
    } catch (err) {
      toast.error(extractErrorAr(err));
    }
  };

  return (
    <div data-testid={testIdPrefix}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#0A1628]">{title}</h1>
          <p className="text-slate-500 mt-1">إجمالي: {items.length}{filterRow && items.length !== visibleItems.length ? ` (ظاهر: ${visibleItems.length})` : ""}</p>
        </div>
        <button
          onClick={openCreate}
          className="aman-btn-light text-sm"
          data-testid={`${testIdPrefix}-create`}
        >
          <Icon name="send" size={16} />
          إضافة جديد
        </button>
      </div>

      {toolbar && (
        <div className="mt-6" data-testid={`${testIdPrefix}-toolbar`}>
          {typeof toolbar === "function" ? toolbar({ items, visibleItems, extra }) : toolbar}
        </div>
      )}

      <div className="mt-8 bg-white border border-slate-100 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="px-4 py-3 text-start font-semibold whitespace-nowrap">{c.label}</th>
                ))}
                <th className="px-4 py-3 text-start font-semibold w-40">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-500">جاري التحميل...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-500">لا توجد عناصر بعد.</td></tr>
              )}
              {!loading && items.length > 0 && visibleItems.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-500">لا توجد نتائج تطابق البحث/الفلتر.</td></tr>
              )}
              {visibleItems.map((row) => (
                <tr key={row.id} data-testid={`${testIdPrefix}-row-${row.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-[#0A1628] align-middle">
                      {c.render ? c.render(row, extra) : row[c.key]}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(row)}
                        className="text-[#0A1628] hover:text-[#00b8cc] text-xs font-semibold"
                        data-testid={`${testIdPrefix}-edit-${row.id}`}
                      >
                        تعديل
                      </button>
                      <button
                        onClick={() => setConfirmDel(row)}
                        className="text-red-600 hover:text-red-700 text-xs font-semibold"
                        data-testid={`${testIdPrefix}-delete-${row.id}`}
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit/Create modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={close}>
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
            data-testid={`${testIdPrefix}-form`}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-[#0A1628]">
                {editing.mode === "create" ? `إنشاء ${title}` : `تعديل ${title}`}
              </h3>
              <button type="button" onClick={close} className="text-slate-400 hover:text-slate-700">
                <Icon name="x" size={22} />
              </button>
            </div>
            <div className="space-y-4">
              {fields.map((f) => (
                <FormField
                  key={f.name}
                  field={f}
                  value={editing.item[f.name]}
                  onChange={(v) => setEditing({ ...editing, item: { ...editing.item, [f.name]: v } })}
                  extra={extra}
                  testId={`${testIdPrefix}-field-${f.name}`}
                />
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={close} className="rounded-full border border-slate-200 px-5 py-2 text-slate-700 hover:bg-slate-50 text-sm">إلغاء</button>
              <button
                type="submit"
                disabled={saving}
                className="aman-btn-light text-sm disabled:opacity-60"
                data-testid={`${testIdPrefix}-save`}
              >
                {saving ? "جاري الحفظ..." : "حفظ"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid={`${testIdPrefix}-delete-confirm`}>
            <h3 className="font-heading text-xl font-bold text-[#0A1628]">تأكيد الحذف</h3>
            <p className="text-slate-600 mt-2">هل أنت متأكد من حذف هذا العنصر؟ لا يمكن التراجع عن الحذف.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="rounded-full border border-slate-200 px-5 py-2 text-sm">إلغاء</button>
              <button onClick={remove} className="rounded-full bg-red-600 text-white px-5 py-2 text-sm hover:bg-red-700" data-testid={`${testIdPrefix}-delete-confirm-btn`}>حذف نهائي</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field renderer
// ---------------------------------------------------------------------------
function FormField({ field, value, onChange, extra, testId }) {
  const label = (
    <label className="block text-sm font-medium text-slate-700 mb-1.5">
      {field.label}
      {field.help && <span className="block text-xs text-slate-400 font-normal mt-0.5">{field.help}</span>}
    </label>
  );
  const cls =
    "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#0A1628] focus:outline-none focus:border-[#00E5FF] focus:ring-4 focus:ring-cyan-100 transition-colors";

  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 cursor-pointer" data-testid={testId}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 rounded border-slate-300 text-[#00b8cc] focus:ring-[#00E5FF]"
        />
        <span className="text-sm text-slate-700">{field.label}</span>
      </label>
    );
  }
  if (field.type === "textarea") {
    return (
      <div>
        {label}
        <textarea
          rows={field.rows || 4}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
          data-testid={testId}
        />
      </div>
    );
  }
  if (field.type === "number") {
    return (
      <div>
        {label}
        <input
          type="number"
          value={value ?? 0}
          min={field.min}
          max={field.max}
          step={field.step || "any"}
          onChange={(e) => onChange(Number(e.target.value))}
          className={cls}
          dir="ltr"
          data-testid={testId}
        />
      </div>
    );
  }
  if (field.type === "select") {
    const opts = typeof field.options === "function" ? field.options(extra) : field.options;
    return (
      <div>
        {label}
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testId}>
          <option value="">اختر...</option>
          {opts?.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }
  if (field.type === "image") {
    return <ImageField label={field.label} value={value} onChange={onChange} testId={testId} autoProcess={field.autoProcess} />;
  }
  if (field.type === "list") {
    // list of strings
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>
        {label}
        <div className="space-y-2" data-testid={testId}>
          {arr.map((v, i) => (
            <div key={i} className="flex gap-2">
              <input
                type="text"
                value={v}
                onChange={(e) => {
                  const next = [...arr]; next[i] = e.target.value; onChange(next);
                }}
                className={cls}
              />
              <button type="button" onClick={() => onChange(arr.filter((_, j) => j !== i))} className="text-red-600 text-xs px-2">حذف</button>
            </div>
          ))}
          <button type="button" onClick={() => onChange([...arr, ""])} className="text-[#00b8cc] text-sm font-semibold">+ إضافة عنصر</button>
        </div>
      </div>
    );
  }
  if (field.type === "kv-list") {
    // list of {label_ar, value_ar} or similar — keys in field.keys
    const arr = Array.isArray(value) ? value : [];
    const keys = field.keys || ["label_ar", "value_ar"];
    return (
      <div>
        {label}
        <div className="space-y-3" data-testid={testId}>
          {arr.map((row, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 items-start">
              {keys.map((k) => (
                <input
                  key={k}
                  type={field.keyTypes?.[k] || "text"}
                  value={row?.[k] ?? ""}
                  onChange={(e) => {
                    const next = [...arr]; next[i] = { ...row, [k]: e.target.value }; onChange(next);
                  }}
                  placeholder={field.placeholders?.[k]}
                  className={cls}
                />
              ))}
              <div className="col-span-2 flex justify-end">
                <button type="button" onClick={() => onChange(arr.filter((_, j) => j !== i))} className="text-red-600 text-xs">حذف</button>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => onChange([...arr, Object.fromEntries(keys.map((k) => [k, ""]))])} className="text-[#00b8cc] text-sm font-semibold">+ إضافة</button>
        </div>
      </div>
    );
  }
  // default: text
  return (
    <div>
      {label}
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={cls}
        dir={field.ltr ? "ltr" : "auto"}
        data-testid={testId}
      />
    </div>
  );
}
